/**
 * Catalog 14 chính tinh × 12 cung trong Tử Vi Đẩu Số.
 *
 * Mỗi sao có:
 *   - category: Bắc Đẩu / Nam Đẩu / Trung Thiên
 *   - element: ngũ hành
 *   - nature: cát / hung / trung tính
 *   - general: ý nghĩa chung của sao
 *   - inPalace: ý nghĩa khi tọa từng cung (12 cung)
 *
 * Nguồn tham khảo: Tử Vi Đẩu Số Toàn Thư, Tử Vi Tinh Quyết.
 * Lưu ý: ý nghĩa rút gọn, cần kết hợp tam phương tứ chính + sao phù trợ + tuần triệt
 * + miếu vượng đắc hãm để luận đầy đủ.
 */

export type PalaceKey =
  | "Mệnh"
  | "Huynh Đệ"
  | "Phu Thê"
  | "Tử Tức"
  | "Tài Bạch"
  | "Tật Ách"
  | "Thiên Di"
  | "Nô Bộc"
  | "Quan Lộc"
  | "Điền Trạch"
  | "Phúc Đức"
  | "Phụ Mẫu";

export type MajorStarKey =
  | "Tử Vi"
  | "Thiên Cơ"
  | "Thái Dương"
  | "Vũ Khúc"
  | "Thiên Đồng"
  | "Liêm Trinh"
  | "Thiên Phủ"
  | "Thái Âm"
  | "Tham Lang"
  | "Cự Môn"
  | "Thiên Tướng"
  | "Thiên Lương"
  | "Thất Sát"
  | "Phá Quân";

export type MajorStarInfo = {
  name: MajorStarKey;
  category: "Bắc Đẩu" | "Nam Đẩu" | "Trung Thiên";
  element: "Mộc" | "Hỏa" | "Thổ" | "Kim" | "Thủy";
  nature: "cát" | "hung" | "trung tính";
  general: string;
  inPalace: Record<PalaceKey, string>;
};

export const PALACE_ORDER: PalaceKey[] = [
  "Mệnh",
  "Phụ Mẫu",
  "Phúc Đức",
  "Điền Trạch",
  "Quan Lộc",
  "Nô Bộc",
  "Thiên Di",
  "Tật Ách",
  "Tài Bạch",
  "Tử Tức",
  "Phu Thê",
  "Huynh Đệ",
];

// Helper for shorter prose authoring
const p = (
  Menh: string,
  PhuMau: string,
  PhucDuc: string,
  DienTrach: string,
  QuanLoc: string,
  NoBoc: string,
  ThienDi: string,
  TatAch: string,
  TaiBach: string,
  TuTuc: string,
  PhuThe: string,
  HuynhDe: string
): Record<PalaceKey, string> => ({
  Mệnh: Menh,
  "Phụ Mẫu": PhuMau,
  "Phúc Đức": PhucDuc,
  "Điền Trạch": DienTrach,
  "Quan Lộc": QuanLoc,
  "Nô Bộc": NoBoc,
  "Thiên Di": ThienDi,
  "Tật Ách": TatAch,
  "Tài Bạch": TaiBach,
  "Tử Tức": TuTuc,
  "Phu Thê": PhuThe,
  "Huynh Đệ": HuynhDe,
});

export const MAJOR_STARS: Record<MajorStarKey, MajorStarInfo> = {
  "Tử Vi": {
    name: "Tử Vi",
    category: "Trung Thiên",
    element: "Thổ",
    nature: "cát",
    general:
      "Đế tinh — sao chủ tể trong Tử Vi Đẩu Số, biểu tượng quyền uy, lãnh đạo, danh tiếng. Có khả năng giải hung tinh khi gặp Tả Phù Hữu Bật, nhưng dễ kiêu căng độc đoán nếu thiếu phụ tinh.",
    inPalace: p(
      "Tướng mạo đường bệ, có khí chất lãnh đạo bẩm sinh, làm chủ một lĩnh vực, sự nghiệp đại quý. Cần Tả Hữu trợ giúp mới phát huy.",
      "Cha mẹ có địa vị xã hội, được kính trọng. Quan hệ tôn nghiêm nhưng có thể cứng nhắc.",
      "Tinh thần cao thượng, trọng danh dự, hưởng phúc lộc. Đam mê quyền lực và địa vị.",
      "Sở hữu nhà cửa khang trang, đất đai có giá trị, gia tộc có danh phận.",
      "Sự nghiệp lớn, vị trí lãnh đạo, quan trường thuận lợi. Lý tưởng cho chính trị, doanh nghiệp lớn, nghề liên quan đến quản lý.",
      "Bạn bè đa số là người có địa vị, được kẻ dưới phục tùng nhưng cần cẩn trọng kẻ tiểu nhân nịnh hót.",
      "Ra ngoài được kính trọng, đi xa phát đạt, quý nhân trợ giúp.",
      "Sức khỏe tổng quát tốt, ít bệnh nặng, nhưng dễ bệnh về tỳ vị nếu lao tâm quá mức.",
      "Tài lộc dồi dào, kiếm tiền chính đáng từ sự nghiệp lớn, không thiếu hụt.",
      "Con cái thông minh, thành đạt, có địa vị. Khó nuôi nếu không có Tả Hữu.",
      "Hôn nhân với người có vị thế cao, nhưng dễ độc đoán trong quan hệ. Vợ/chồng có tính cương nghị.",
      "Anh chị em có người làm lớn, hỗ trợ tốt. Bản thân thường là trưởng hoặc giữ vai trò lãnh đạo trong gia tộc."
    ),
  },
  "Thiên Cơ": {
    name: "Thiên Cơ",
    category: "Nam Đẩu",
    element: "Mộc",
    nature: "cát",
    general:
      "Trí tuệ tinh — sao mưu lược, thông minh, đa tài, ưa tính toán và phân tích. Tính cách linh hoạt, hay thay đổi, thích hợp công việc cần trí óc và sự tinh tế.",
    inPalace: p(
      "Người thông minh nhanh nhạy, mưu trí cao, đa tài đa nghệ. Tính tình hay đổi, dễ bị cuốn theo cảm hứng.",
      "Cha mẹ có học thức, biết ứng xử khôn khéo. Quan hệ gần gũi nhưng đôi khi xa cách lý trí.",
      "Tâm tư phong phú, hay suy nghĩ nhiều, thích triết lý và tôn giáo. Đôi khi căng thẳng vì lo toan quá độ.",
      "Nhà cửa hay thay đổi, có khả năng kinh doanh bất động sản nhưng vận đất không bền.",
      "Hợp nghề trí óc: tham mưu, kế toán, lập trình, tư vấn, giáo dục, viết lách. Thường thay đổi nghề.",
      "Bạn bè trí thức, giúp đỡ về mặt ý tưởng. Quan hệ thân thiết không nhiều.",
      "Ra ngoài giỏi ứng biến, hợp di chuyển, đi xa lập nghiệp. Hay thay đổi nơi ở.",
      "Yếu thần kinh, dễ mất ngủ, suy nghĩ nhiều dẫn đến đau đầu, dạ dày.",
      "Tiền đến từ trí tuệ, công việc đầu óc; tài lộc không lớn nhưng đều đặn.",
      "Con cái thông minh, lanh lợi, học giỏi. Đôi khi không gần gũi.",
      "Vợ/chồng tinh tế, hay tính toán. Tình cảm dễ thay đổi, cần học cách bao dung.",
      "Anh chị em đông, có người tài giỏi nhưng quan hệ không ổn định, dễ tan hợp."
    ),
  },
  "Thái Dương": {
    name: "Thái Dương",
    category: "Trung Thiên",
    element: "Hỏa",
    nature: "cát",
    general:
      "Sao Mặt Trời — biểu tượng nam tính, danh tiếng, công danh sự nghiệp, quang minh chính đại. Miếu vượng tại Tỵ-Ngọ-Thìn-Mão, hãm tại Tuất-Hợi-Tý-Sửu (sinh ban đêm Thái Dương lạc hãm dễ vất vả).",
    inPalace: p(
      "Hào sảng, quang minh, có chí lớn, thích giúp đỡ người. Miếu vượng thì danh tiếng lan xa; lạc hãm thì lao tâm khổ tứ.",
      "Đại diện cha (chủ về phụ thân). Cha có địa vị, được trọng vọng. Lạc hãm thì cha vất vả, sớm mất, hoặc xa cách.",
      "Tâm hồn rộng rãi, thích hoạt động, đam mê công việc xã hội, danh dự là quan trọng nhất.",
      "Nhà cửa rộng rãi, sáng sủa, hướng tốt; lạc hãm thì nhà cửa thiếu sáng hoặc khó giữ.",
      "Hợp công việc xã hội, chính trị, truyền thông, giảng dạy, lãnh đạo công cộng. Phụ nữ làm sự nghiệp ngoài xã hội.",
      "Kết giao rộng, được nhiều người biết đến, có quý nhân danh tiếng giúp đỡ.",
      "Đi xa phát đạt, danh tiếng lan tỏa, hợp xuất ngoại lập nghiệp.",
      "Bệnh về mắt (sao chủ mắt), tim mạch, huyết áp cao. Lạc hãm tăng nguy cơ.",
      "Tiền đến từ danh tiếng, công việc công khai; tài lộc rộng rãi nhưng tiêu cũng nhiều.",
      "Con cái có tài, đặc biệt là con trai phát đạt. Lạc hãm thì khó nuôi con trai.",
      "Đối với nữ: chồng có địa vị, danh tiếng, là người chủ gia đình. Đối với nam: vợ tháo vát.",
      "Anh chị em có người làm sự nghiệp lớn, đặc biệt anh trai có quyền uy."
    ),
  },
  "Vũ Khúc": {
    name: "Vũ Khúc",
    category: "Bắc Đẩu",
    element: "Kim",
    nature: "cát",
    general:
      "Tài tinh — sao tài chính, cương nghị, quyết đoán, cô độc. Chủ về tiền bạc, võ chức, hành động dứt khoát. Tính cách thẳng thắn, không thích lãng mạn.",
    inPalace: p(
      "Cương nghị, quyết đoán, tự lập từ sớm. Khả năng kiếm tiền tốt nhưng tính cô độc, hôn nhân muộn hoặc khó.",
      "Cha mẹ nghiêm khắc, cứng cỏi. Quan hệ cứng nhắc, ít tình cảm bộc lộ.",
      "Lo nghĩ về vật chất, ít hưởng thụ, sống thực dụng. Tâm tính khá khô khan.",
      "Có tài sản từ tiết kiệm và đầu tư cẩn thận, nhà cửa vững chắc.",
      "Hợp nghề tài chính, ngân hàng, võ nghiệp, công an, kỹ thuật, kim hoàn. Quyết đoán trong sự nghiệp.",
      "Bạn bè cương trực, chân thật nhưng ít. Không hợp với người mơ mộng, lãng mạn.",
      "Đi xa kiếm tiền tốt, thích hợp lập nghiệp ở vùng đất mới.",
      "Bệnh về phổi, khí quản, vết thương do kim khí, tai nạn xe cộ.",
      "Sao tài chính số 1 — tiền của dồi dào, kiếm tiền giỏi, biết tiết kiệm và đầu tư.",
      "Con cái ít, thường 1-2 con. Quan hệ kỷ luật, ít tình cảm bộc lộ.",
      "Phối ngẫu cô khắc — hôn nhân muộn (sau 30) hoặc khó. Cần lấy người có tài chính độc lập, không mơ mộng.",
      "Anh em ít, thường 1-2. Quan hệ thực dụng nhưng giúp đỡ tài chính khi cần."
    ),
  },
  "Thiên Đồng": {
    name: "Thiên Đồng",
    category: "Nam Đẩu",
    element: "Thủy",
    nature: "cát",
    general:
      "Phúc tinh — sao hưởng phúc, ôn hòa, thiện lương, lạc quan. Tính cách hiền hậu, nhân ái, ưa hòa bình, không thích tranh đấu. Dễ ỷ lại nếu không có sát tinh kích thích.",
    inPalace: p(
      "Tính tình ôn hòa, lạc quan, hưởng phúc. Đôi khi ỷ lại, lười nhác, thiếu chí tiến thủ. Cần sát tinh để kích phát.",
      "Cha mẹ hiền lành, từ ái. Quan hệ hòa thuận, êm ấm.",
      "Hưởng phúc lộc, sống an nhàn, tâm tính bao dung. Ngại đối đầu, thích hưởng thụ nhẹ nhàng.",
      "Nhà cửa ấm cúng, đầm ấm. Hưởng tài sản tổ tiên hoặc được người khác tặng.",
      "Hợp công việc dịch vụ, dân sinh, y tế phúc lợi, ngành ăn uống nhẹ nhàng. Không thích áp lực cao.",
      "Bạn bè thân thiết, được giúp đỡ. Quan hệ vui vẻ không bon chen.",
      "Ra ngoài bình an, gặp quý nhân giúp đỡ, không gặp tai nạn lớn.",
      "Sức khỏe tốt, ít bệnh nặng. Cần đề phòng bệnh thận, đường tiết niệu.",
      "Tiền đến tự nhiên, không phải lao tâm. Thường có người chu cấp hoặc thừa kế.",
      "Con cái hiếu thảo, lành tính. Quan hệ vui vẻ, đầm ấm.",
      "Phối ngẫu hiền hậu, gia đình êm ấm. Tình cảm chân thành, không sóng gió.",
      "Anh chị em hòa thuận, giúp đỡ lẫn nhau, đông đảo và tình cảm."
    ),
  },
  "Liêm Trinh": {
    name: "Liêm Trinh",
    category: "Bắc Đẩu",
    element: "Hỏa",
    nature: "trung tính",
    general:
      "Tù tinh — sao quan lộc thứ hai, đào hoa kiêm chính trực. Tính cách phức tạp: vừa nguyên tắc vừa phong lưu, ưa tự do, dễ phạm pháp luật nếu gặp hung tinh. Hợp với nghề luật, cảnh sát, nghệ thuật.",
    inPalace: p(
      "Cá tính mạnh, nguyên tắc cao, có sức hút (đào hoa), nhưng tâm trạng phức tạp. Đời thăng trầm, dễ gặp tai họa.",
      "Cha mẹ có cá tính, không thoải mái với con. Quan hệ căng thẳng hoặc xa cách.",
      "Tâm hồn phức tạp, hay suy tư về tình cảm và đạo đức. Đôi khi mâu thuẫn giữa lý trí và bản năng.",
      "Tài sản hay biến động, có khi giàu có khi mất. Nhà cửa có chuyện thị phi.",
      "Quan trường thăng tiến nhanh nếu chính trực, hợp nghề luật, cảnh sát, võ nghiệp, nghệ thuật biểu diễn. Gặp Sát tinh dễ thị phi quan tụng.",
      "Bạn bè đa dạng, có người trợ giúp lớn nhưng cũng có kẻ liên lụy. Cần chọn lọc.",
      "Đi xa hay gặp chuyện ngoài ý muốn, tai nạn, kiện tụng. Cần thận trọng.",
      "Bệnh về máu, tim, phụ khoa (nữ). Dễ tai nạn đổ máu, phẫu thuật.",
      "Tài lộc thăng trầm, có khi đột phát đột bại. Hợp đầu tư mạo hiểm nhưng cần kỷ luật.",
      "Con cái có cá tính mạnh, khó dạy. Đôi khi gặp chuyện không như ý.",
      "Đào hoa mạnh — hôn nhân nhiều biến cố, dễ ngoại tình hoặc bị lụy về tình. Phối ngẫu có cá tính.",
      "Anh chị em có người gặp chuyện thị phi pháp luật, quan hệ không êm đẹp."
    ),
  },
  "Thiên Phủ": {
    name: "Thiên Phủ",
    category: "Nam Đẩu",
    element: "Thổ",
    nature: "cát",
    general:
      "Tài khố — sao kho tiền, ổn định, bảo thủ, từ thiện. Tính cách trầm ổn, đáng tin, biết quản lý tài sản, ưa hòa bình. Là phó vương của Tử Vi.",
    inPalace: p(
      "Tướng mạo trầm ổn, đôn hậu, có khả năng quản lý. Tính cách bảo thủ, đáng tin cậy, hợp làm lãnh đạo cấp 2.",
      "Cha mẹ hiền hậu, có tài sản, biết tích lũy. Quan hệ ấm áp.",
      "Tâm tư trầm tĩnh, biết hưởng thụ vừa phải, không cực đoan.",
      "Sao kho tiền — sở hữu tài sản lớn, đất đai, nhà cửa nhiều. Bất động sản phát đạt.",
      "Hợp tài chính, ngân hàng, kho bạc, quản lý tài sản, kế toán trưởng. Vị trí phó/cấp 2 ổn định.",
      "Bạn bè đáng tin, giúp đỡ thực chất. Quan hệ lâu dài.",
      "Ra ngoài an toàn, được kính trọng, đi đâu cũng có chỗ dung thân.",
      "Sức khỏe ổn định, ít bệnh, sống thọ. Cẩn thận tỳ vị, dạ dày.",
      "Tiền của dồi dào, biết tích lũy, không bị thất thoát. Tài sản vững chắc.",
      "Con cái nhiều và hiền lành, có tài sản. Hưởng phúc con cháu.",
      "Phối ngẫu đôn hậu, có tài sản hoặc gia thế. Hôn nhân bền vững.",
      "Anh chị em hòa thuận, có người giàu có. Quan hệ ổn định, hỗ trợ tài chính."
    ),
  },
  "Thái Âm": {
    name: "Thái Âm",
    category: "Trung Thiên",
    element: "Thủy",
    nature: "cát",
    general:
      "Sao Mặt Trăng — biểu tượng nữ tính, mẹ, vợ, gia đình, tài sản kín đáo, thẩm mỹ. Miếu vượng tại Hợi-Tý-Tuất-Dậu, hãm tại Tỵ-Ngọ-Thìn-Mão.",
    inPalace: p(
      "Tính tình ôn nhu, có thẩm mỹ cao, đa tình. Nữ giới càng đẹp đẽ tinh tế. Miếu vượng phú quý, hãm thì cô độc.",
      "Đại diện mẹ. Mẹ hiền hậu, đảm đang. Lạc hãm thì mẹ vất vả, sớm mất, hoặc xa cách.",
      "Tâm hồn lãng mạn, yêu nghệ thuật, thiên nhiên. Đôi khi sầu cảm.",
      "Tài sản kín đáo, nhà cửa đẹp, thẩm mỹ cao. Hưởng nhà cửa từ mẹ hoặc nữ giới.",
      "Hợp nghề nghệ thuật, văn hóa, thời trang, du lịch, dịch vụ phụ nữ. Phụ nữ làm sự nghiệp nội trợ kết hợp.",
      "Bạn bè đa số là nữ, hoặc người có thẩm mỹ. Quan hệ tinh tế.",
      "Đi xa phát đạt, đặc biệt về đêm hoặc nơi gần nước. Hợp xuất ngoại du lịch, dịch vụ.",
      "Bệnh phụ khoa (nữ), thận, mắt, tâm lý trầm cảm. Lạc hãm tăng nặng.",
      "Tiền đến kín đáo, từ tiết kiệm, đầu tư bất động sản, tài sản gia tộc. Nữ giới giỏi quản lý tài chính.",
      "Con cái xinh đẹp, đặc biệt con gái phát đạt. Lạc hãm thì khó có con gái.",
      "Đối với nam: vợ xinh đẹp, hiền dịu, đảm đang. Đối với nữ: chồng tinh tế, có thẩm mỹ.",
      "Chị em gái phát đạt, hỗ trợ tốt. Quan hệ tình cảm đậm đà."
    ),
  },
  "Tham Lang": {
    name: "Tham Lang",
    category: "Bắc Đẩu",
    element: "Mộc",
    nature: "trung tính",
    general:
      "Đào hoa tinh — sao đào hoa số 1, đa tài đa nghệ, ưa giao tiếp, tham vọng cao. Tính cách phong lưu, thích hưởng thụ, dễ sa vào tửu sắc. Gặp Hỏa Linh thì phát đột ngột.",
    inPalace: p(
      "Đa tài đa nghệ, có sức hút lớn, giỏi giao tiếp. Tham vọng cao nhưng dễ phân tâm bởi nhiều thú vui.",
      "Cha mẹ có cá tính phong lưu, hoặc nhiều mối quan hệ phức tạp.",
      "Yêu thích hưởng thụ, nghệ thuật, ăn uống. Tâm hồn đa dạng nhưng dễ sa đà.",
      "Bất động sản hay thay đổi, kinh doanh nhà cửa cần thận trọng.",
      "Hợp giao tiếp, ngoại giao, kinh doanh giải trí, nghệ thuật, ẩm thực, du lịch. Gặp Hỏa Linh thành 'Tham Hỏa cách' phát đột ngột.",
      "Bạn bè đông đảo, đa dạng tầng lớp. Giao thiệp rộng nhưng quan hệ không sâu.",
      "Ra ngoài đắc nhân tâm, đi đâu cũng được hoan nghênh, gặp nhiều cơ hội.",
      "Bệnh sinh dục, gan, da liễu. Cẩn thận tửu sắc gây hại.",
      "Tiền đến từ giao tiếp, hưởng thụ, đôi khi phát ngẫu nhiên. Tiêu pha hào phóng.",
      "Con cái thông minh đa tài, đào hoa. Cần dạy dỗ kỹ về kỷ luật.",
      "Đào hoa cực mạnh — nhiều mối tình, kết hôn muộn hoặc nhiều lần. Phối ngẫu phong lưu, có sức hút.",
      "Anh chị em đông, đa dạng, quan hệ vui vẻ nhưng đôi khi phức tạp."
    ),
  },
  "Cự Môn": {
    name: "Cự Môn",
    category: "Bắc Đẩu",
    element: "Thủy",
    nature: "hung",
    general:
      "Khẩu thiệt tinh — sao thị phi, miệng lưỡi, nghi ngờ, tranh luận. Tính cách thâm thúy, đa nghi, giỏi biện luận. Hợp nghề dùng miệng (giáo viên, luật sư, MC, sales). Hung tính khi lạc hãm hoặc gặp Sát tinh.",
    inPalace: p(
      "Đa nghi, hay biện luận, miệng lưỡi sắc bén. Có chiều sâu nhưng dễ thị phi. Cần Hóa Lộc/Quyền/Khoa giải.",
      "Quan hệ với cha mẹ hay tranh cãi, mâu thuẫn miệng lưỡi. Cha mẹ khó tính.",
      "Tâm tư nặng nề, hay suy nghĩ tiêu cực, nghi ngờ. Khó hưởng phúc nếu không tu tâm.",
      "Nhà cửa hay tranh chấp, kiện tụng về đất đai, hàng xóm thị phi.",
      "Hợp nghề dùng lời nói: giáo viên, luật sư, diễn giả, MC, bán hàng, ngoại giao, ngôn luận. Cần lời thật khéo.",
      "Bạn bè hay thị phi, nói xấu sau lưng, dễ phản bội. Cần thận trọng kết giao.",
      "Đi xa hay gặp tranh cãi, kiện tụng. Cẩn thận lời ăn tiếng nói.",
      "Bệnh về miệng, răng, dạ dày, tiêu hóa. Tâm lý hay căng thẳng.",
      "Tiền đến từ nghề nghiệp dùng miệng. Hay có tranh chấp tài chính, cẩn thận hợp đồng.",
      "Con cái khó dạy, hay cãi lại. Quan hệ nhiều mâu thuẫn lời nói.",
      "Hôn nhân nhiều cãi vã, miệng lưỡi không ngọt ngào. Cần học cách giao tiếp.",
      "Anh chị em hay tranh cãi, thị phi, quan hệ không êm đẹp. Cẩn thận chia tài sản."
    ),
  },
  "Thiên Tướng": {
    name: "Thiên Tướng",
    category: "Nam Đẩu",
    element: "Thủy",
    nature: "cát",
    general:
      "Ấn tinh — sao quan ấn, trợ lý, công bằng, lễ nghi. Tính cách trung thành, nghiêm túc, biết phục tùng cấp trên và quan tâm cấp dưới. Hợp vai trò phụ tá đắc lực.",
    inPalace: p(
      "Tướng mạo đoan chính, lễ độ, trọng nghĩa khí. Có khả năng làm trợ lý đắc lực, không thích đứng đầu.",
      "Cha mẹ trọng lễ nghĩa, có địa vị xã hội. Quan hệ kính trọng, đứng đắn.",
      "Tâm hồn ngay thẳng, thích công bằng, không chấp nhận sai trái.",
      "Nhà cửa đoan trang, có giá trị, bảo dưỡng tốt.",
      "Hợp vai trò phụ tá, thư ký, trưởng phòng, thẩm phán, công chức. Sự nghiệp ổn định, thăng tiến đều.",
      "Bạn bè trung thành, có người trợ giúp đắc lực, không phản bội.",
      "Ra ngoài có quý nhân giúp đỡ, được kính trọng vì sự ngay thẳng.",
      "Sức khỏe tốt, cẩn thận thận, bàng quang, hệ tiết niệu.",
      "Tài lộc ổn định từ chức vụ, lương bổng. Không phú quý đột ngột nhưng bền.",
      "Con cái hiếu thảo, biết lễ nghĩa. Quan hệ tốt đẹp.",
      "Phối ngẫu trung thành, đoan chính, là chỗ dựa vững chắc. Hôn nhân bền lâu.",
      "Anh chị em trợ giúp lẫn nhau, đoàn kết, lễ độ."
    ),
  },
  "Thiên Lương": {
    name: "Thiên Lương",
    category: "Nam Đẩu",
    element: "Thổ",
    nature: "cát",
    general:
      "Ấm tinh — sao bảo hộ, từ thiện, tôn giáo, nguyên tắc đạo đức. Tính cách chính trực, từ bi, có khả năng hóa giải tai họa. Đôi khi bảo thủ, cố chấp.",
    inPalace: p(
      "Chính trực, từ thiện, có nguyên tắc đạo đức cao. Tướng mạo già dặn, đôn hậu. Có khả năng hóa giải hiểm nguy.",
      "Cha mẹ có đạo đức, từ thiện. Quan hệ tôn kính, được giáo dục đạo đức tốt.",
      "Tâm hồn cao thượng, hướng thiện, có lòng từ bi. Đam mê triết lý, tôn giáo.",
      "Nhà cửa thanh tịnh, có thể gần nơi tôn giáo, công viên. Ổn định.",
      "Hợp y tế, từ thiện, giáo dục, tôn giáo, tư vấn, công chức. Gặp tai họa thường được giải.",
      "Bạn bè là người có đạo đức, hỗ trợ tinh thần. Quý nhân bảo hộ.",
      "Đi xa được phù hộ, gặp nguy hóa giải. Hợp đi xa lập nghiệp, công tác xã hội.",
      "Sức khỏe tốt, sống thọ, gặp bệnh dễ chữa. Có quý nhân giúp.",
      "Tài lộc đến chậm nhưng bền, không phát đột ngột. Hợp giúp đỡ người để tích đức.",
      "Con cái muộn (cao tuổi mới có), nhưng hiếu thảo, có đạo đức.",
      "Hôn nhân muộn, phối ngẫu có đạo đức, hơn tuổi hoặc già dặn. Quan hệ tôn kính.",
      "Anh chị em là chỗ dựa tinh thần, có người đạo đức. Quan hệ tôn trọng."
    ),
  },
  "Thất Sát": {
    name: "Thất Sát",
    category: "Nam Đẩu",
    element: "Kim",
    nature: "hung",
    general:
      "Tướng tinh — sao tướng quân (kiêm hành Hỏa), dũng mãnh, quyết đoán, độc lập. Tính cách cứng cỏi, không sợ khó, dám nghĩ dám làm, nhưng thiếu kiên nhẫn và dễ cô độc.",
    inPalace: p(
      "Cá tính mạnh mẽ, độc lập, quyết đoán. Đời nhiều biến động, tự lập từ sớm. Cô độc, ít chia sẻ.",
      "Cha mẹ nghiêm khắc, hoặc xa cách. Quan hệ căng thẳng, ít hòa hợp.",
      "Tâm tính cứng cỏi, ít hưởng phúc, lao tâm khổ tứ. Khó thư giãn.",
      "Nhà cửa hay biến động, di chuyển nhiều. Cẩn thận đầu tư bất động sản.",
      "Hợp võ nghiệp, công an, quân đội, kỹ thuật, thể thao, kinh doanh mạo hiểm. Quyết đoán, dám phá cách.",
      "Bạn bè ít, nhưng người chiến hữu trung thành. Không thích giao tiếp xã giao.",
      "Đi xa nhiều biến động, có thể tai nạn. Cần thận trọng nhưng dũng cảm.",
      "Bệnh tật nguy hiểm: tai nạn, phẫu thuật, bệnh phổi, vết thương kim khí.",
      "Tiền đến từ phấn đấu mạo hiểm, có khi đột phát đột bại. Hợp đầu tư cao rủi ro.",
      "Con cái ít, thường 1-2. Cá tính mạnh, độc lập sớm.",
      "Hôn nhân muộn hoặc sóng gió. Phối ngẫu cá tính, cứng đầu. Cần học cách nhường nhịn.",
      "Anh chị em ít, quan hệ căng thẳng hoặc xa cách. Mỗi người tự lập."
    ),
  },
  "Phá Quân": {
    name: "Phá Quân",
    category: "Bắc Đẩu",
    element: "Thủy",
    nature: "hung",
    general:
      "Hao tinh — sao phá hoại, đột phá, đổi mới, mạo hiểm. Tính cách phá cũ lập mới, không tuân thủ truyền thống, dám làm điều chưa ai làm. Đời nhiều biến động.",
    inPalace: p(
      "Cá tính mạnh, dám phá cách, sáng tạo. Đời nhiều biến cố, hay đổi mới. Khó theo lối mòn, tự lập sớm.",
      "Cha mẹ ly biệt, hoặc quan hệ trắc trở. Hay rời xa nhà sớm.",
      "Tâm hồn không yên, hay tìm kiếm cái mới. Khó hưởng phúc theo nghĩa truyền thống.",
      "Nhà cửa hay sửa chữa, đổi mới, hoặc bán đi mua lại. Tài sản hay biến động.",
      "Hợp nghề sáng tạo, đổi mới, công nghệ mới, khởi nghiệp, phá cách. Không hợp công việc lặp lại.",
      "Bạn bè đa dạng nhưng quan hệ hay đứt gãy. Cẩn thận bị phản bội tài chính.",
      "Đi xa nhiều biến động, lập nghiệp ở nơi mới. Hay đổi nơi ở.",
      "Bệnh tật bất ngờ, tai nạn, vết thương lớn. Cẩn thận giao thông.",
      "Tài lộc lên xuống mạnh, có khi đột phát đột bại. Hợp khởi nghiệp, đầu tư công nghệ mới.",
      "Con cái ít, hoặc khó nuôi. Quan hệ phá cách, không truyền thống.",
      "Hôn nhân nhiều sóng gió, dễ ly hôn rồi tái hôn. Phối ngẫu cá tính phá cách.",
      "Anh chị em xa cách, ly tán, mỗi người một nơi. Quan hệ hay đứt gãy."
    ),
  },
};

export function getMajorStarInfo(name: string): MajorStarInfo | null {
  if (name in MAJOR_STARS) {
    return MAJOR_STARS[name as MajorStarKey];
  }
  return null;
}

export function isMajorStar(name: string): boolean {
  return name in MAJOR_STARS;
}
